import { defineComponent, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { usePaymentMethodsExceptionLimitStore } from '@/store/ps-management/payment-method-exception-limits.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import {
  PaymentMethodExceptionLimit,
  PaymentMethodExceptionLimitType,
  PaymentMethodInfoField,
  PaymentMethodInfoLabel,
} from '@/models/ps-management/exception-limit.model';
import LimitException from '@/layouts/ps-management-page/limit-exception-settings/limit-exception/limit-exception.vue';
import { TagComponentTypes } from '@/models/app-common.model';
import FallbackImage from '@/components/fallback-image/fallback-image.vue';

export default defineComponent({
  name: 'LimitExceptionSettings',
  components: {
    LimitException,
    FallbackImage,
  },
  setup() {
    const paymentMethodsExceptionLimitStore = usePaymentMethodsExceptionLimitStore();
    const {
      isExceptionLimitSettingsSidebarVisible,
      extendedPaymentMethodExceptionLimitList,
      exceptionLimitList,
      isExceptionLimitListLoading,
      currentPaymentMethodId,
      currentPaymentMethod,
      exceptionLimitHeader,
      paymentMethodOptionType,
    } = storeToRefs(paymentMethodsExceptionLimitStore);
    const {
      closeExceptionLimitSettings,
      getPaymentMethodExceptionLimit,
      getPaymentMethodGeneralLimit,
      addExceptionLimitItem,
    } = paymentMethodsExceptionLimitStore;

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    onMounted(() => {
      if (!currentPaymentMethodId.value) {
        return;
      }

      getPaymentMethodExceptionLimit(currentPaymentMethodId.value);
      getPaymentMethodGeneralLimit(currentPaymentMethodId.value);
    });

    const isAddLimitExceptionButtonVisible = computed<boolean>(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.CREATE_METHOD_EXTENDED_LIMIT],
    );

    const infoFieldList = computed<PaymentMethodInfoField[]>(() => {
      const fieldList: PaymentMethodInfoField[] = [
        {
          label: PaymentMethodInfoLabel.INTERNAL_NAME,
          value: currentPaymentMethod.value.paymentSystem,
        },
        {
          label: PaymentMethodInfoLabel.PUBLIC_NAME,
          value: currentPaymentMethod.value.publicName,
        },
      ];

      if (
        paymentMethodOptionType.value === PaymentMethodExceptionLimitType.WITHDRAWAL &&
        currentPaymentMethod.value.billingProviderName
      ) {
        fieldList.push({
          label: PaymentMethodInfoLabel.BILLING_PROVIDER,
          value: currentPaymentMethod.value.billingProviderName,
        });
      }

      return fieldList;
    });

    const updateExceptionLimitItem = (updatedLimitItem: PaymentMethodExceptionLimit): void => {
      const index = exceptionLimitList.value.findIndex(
        ({ id }: PaymentMethodExceptionLimit) => id === updatedLimitItem.id,
      );

      if (index !== -1) {
        exceptionLimitList.value[index] = updatedLimitItem;
      }
    };

    const isAddExceptionLimitItemEnable = (currency: string): boolean => {
      const limitGroup = extendedPaymentMethodExceptionLimitList.value.find(group => group.currency === currency);
      const allLimitsHaveId = limitGroup ? limitGroup.limitList.every(limit => limit.id) : true;

      return Boolean(limitGroup) && allLimitsHaveId;
    };

    return {
      exceptionLimitHeader,
      isExceptionLimitSettingsSidebarVisible,
      isExceptionLimitListLoading,
      extendedPaymentMethodExceptionLimitList,
      addExceptionLimitItem,
      closeExceptionLimitSettings,
      currentPaymentMethod,
      isAddLimitExceptionButtonVisible,
      updateExceptionLimitItem,
      isAddExceptionLimitItemEnable,
      infoFieldList,
      TagComponentTypes,
    };
  },
});
